import React, { useState, useMemo } from 'react';
import API from '../../../utils/API';
import { formatDisplayDate } from '../../../utils/formatDisplayDate';
import './DonationsTable.css';
import { Link, useLocation } from 'react-router-dom';


interface DonationsTableProps {
  donations: any[];
  // onShowReport: () => void;
  onDownloadReport: () => void;
  duration?: string;
  setDuration?: (duration: string) => void;
  fundraiser?: string;
  setFundraiser?: (fundraiser: string) => void;
  campaigns?: any[];
  npID?: string;
  title?: string;
  enableCustomDate?: boolean;
  customStartDate?: string;
  setCustomStartDate?: (date: string) => void;
  customEndDate?: string;
  setCustomEndDate?: (date: string) => void;
  isDashboardPage?: boolean;
  isOnboardingComplete?: boolean;
  // Overrides the default npID-scoped report fetch (getAllDonationsForNp) —
  // needed for views not scoped to a single nonprofit, e.g. a donor's own
  // fundraiser donations.
  fetchReportData?: (duration: string, fundraiser: string, tz: string) => Promise<any[]>;
  // Donor's own "Donation history" view (My Fundraisers) hides these: zip
  // code isn't useful to a donor tracking their own fundraiser, and a CSV
  // export of supporter PII isn't something we want individual donors able
  // to pull for security reasons — nonprofit's Manage Donations keeps both.
  hideZipCode?: boolean;
  hideDownloadReport?: boolean;
}

const DonationsTable: React.FC<DonationsTableProps> = ({
  donations,
  // onShowReport,
  onDownloadReport,
  duration = 'year',
  setDuration,
  fundraiser = 'all',
  setFundraiser,
  campaigns = [],
  npID,
  title = 'Donations',
  enableCustomDate = false,
  customStartDate = '',
  setCustomStartDate,
  customEndDate = '',
  setCustomEndDate,
  isDashboardPage = true,
  isOnboardingComplete = true,
  fetchReportData,
  hideZipCode = false,
  hideDownloadReport = false,
}) => {
  const [sortField, setSortField] = useState<string>('createdAt');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  // Filter donations based on custom date if selected
  // detect viewer timezone
  const userTz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

  const filteredDonations = useMemo(() => {
    // Keep the client-side custom-date filter commented out because the backend
    // already filters by timezone and this extra pass can hide valid donations.
    // if (duration === 'custom' && customStartDate && customEndDate) {
    //   // Compare by YYYY-MM-DD in viewer's timezone to avoid timezone shifts
    //   const start = customStartDate; // format: YYYY-MM-DD from <input type="date" />
    //   const end = customEndDate;
    //   const fmt = new Intl.DateTimeFormat('en-US', {
    //     timeZone: userTz,
    //     year: 'numeric',
    //     month: '2-digit',
    //     day: '2-digit',
    //   });
    //
    //   return donations.filter((donation) => {
    //     const d = new Date((donation as any).createdAt || (donation as any).date);
    //     const donationYMD = fmt.format(d); // yields YYYY-MM-DD
    //     return donationYMD >= start && donationYMD <= end;
    //   });
    // }
    return donations;
  }, [donations]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(amount);
  };

  // const formatDate = (dateString: string) => {
  //   if (!dateString) return '';
  //   const d = new Date(dateString);
  //   return new Intl.DateTimeFormat('en-US', {
  //     timeZone: userTz,
  //     month: '2-digit',
  //     day: '2-digit',
  //     year: '2-digit'
  //   }).format(d);
  // };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'Completed':
        return 'success';
      case 'completed':
        return 'success';
      case 'succeeded':
        return 'success';
      case 'active':
        return 'success';
      case 'trailing':
        return 'success';
      case 'created':
        return 'warning';
      case 'Failed':
        return 'danger';
      case 'failed':
        return 'danger';
      case 'cancelled':
        return 'danger';
      default:
        return 'secondary';
    }
  };

  const getTypeColor = (type: string) => {
    return type === 'Recurring' ? '#4AD991' : '#6F6CFF ';
  };

  const downloadCSV = (content: string, filename: string) => {
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const convertToCSV = (data: any[]) => {
    if (data.length === 0) return '';
    const headers = ['ID', 'Date', 'Amount', 'First Name', 'Last Name', 'Email', 'Zip Code', 'Fundraiser', 'Type', 'Status'];
    const csvRows = [headers.join(',')];
    data.forEach((item, index) => {
      const row = [
        item._id || index.toString(),
        item.createdAt || item.date,
        (item.amount || 0).toString(),
        item.name || item.firstName || '',
        item.lastName || '',
        item.donorEmail || item.email || '',
        item.zipCode || '',
        item.campaignID ? (campaigns.find(c => c.id === item.campaignID)?.name || item.campaignID) : 'Direct Donation',
        item.type || 'One time',
        item.status || 'Completed'
      ];
      csvRows.push(row.join(','));
    });
    return csvRows.join('\n');
  };

  const handleDownloadReport = async () => {
    if (!npID && !fetchReportData) {
      console.error('npID (or fetchReportData) is required for downloading report');
      return;
    }
    try {
      let allDonations;
      if (duration === 'custom' && enableCustomDate && customStartDate && customEndDate) {
        // Use filtered donations for custom date to ensure consistency
        allDonations = filteredDonations;
      } else if (fetchReportData) {
        allDonations = await fetchReportData(duration, fundraiser, userTz);
      } else {
        const query = `duration=${duration}&fundraiser=${fundraiser}`;
        // include viewer tz so server can compute day boundaries in user's timezone if desired
        const tzQuery = `${query}&tz=${encodeURIComponent(userTz)}`;
        const response = await API.getAllDonationsForNp(npID as string, tzQuery);
        allDonations = response.data;
      }
      const csvContent = convertToCSV(allDonations);
      downloadCSV(csvContent, `all_${title.toLowerCase()}.csv`);
    } catch (error) {
      console.error('Error downloading report:', error);
    }
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedDonations = [...filteredDonations].sort((a, b) => {
    let aValue: any;
    let bValue: any;

    if (sortField === 'createdAt') {
      aValue = new Date((a as any).createdAt || (a as any).date);
      bValue = new Date((b as any).createdAt || (b as any).date);
      return sortDirection === 'asc' ? aValue.getTime() - bValue.getTime() : bValue.getTime() - aValue.getTime();
    } else if (sortField === 'amount') {
      aValue = (a as any).amount;
      bValue = (b as any).amount;
    } else if (sortField === 'name') {
      aValue = (a as any).name || (a as any).firstName || '';
      bValue = (b as any).name || (b as any).firstName || '';
    } else if (sortField === 'lastName') {
      aValue = (a as any).lastName || '';
      bValue = (b as any).lastName || '';
    } else if (sortField === 'donorEmail') {
      aValue = (a as any).donorEmail || (a as any).email || '';
      bValue = (b as any).donorEmail || (b as any).email || '';
    } else {
      aValue = (a as any)[sortField];
      bValue = (b as any)[sortField];
    }

    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return sortDirection === 'asc'
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    }

    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
    }

    return 0;
  });

  const totalAmount = filteredDonations.reduce((sum, donation) => sum + ((donation as any).amount || 0), 0);
  const path = useLocation().pathname.replace("/", "");

  return (
    <div className="donations-table-container">
      <div className="d-t-c-t">
        <h3 className="table-title">{title}</h3>
      </div>

      <div className="table-header">
        <div className="table-title-section">
          <div className="table-filters">
            {setDuration && (
              <select
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
                className="filter-select"
              >
                <option value="yearToDate">Year to Date</option>
                <option value="monthToDate">Month to Date</option>
                {enableCustomDate && <option value="custom">Custom Date</option>}
                <option value="all">All Time</option>
              </select>
            )}
            {enableCustomDate && duration === 'custom' && setCustomStartDate && setCustomEndDate && (
              <div style={{ display: 'flex', gap: '10px' }}>
                <input
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  className="filter-select" />
                <input
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  className="filter-select" />
              </div>
            )}
            {setFundraiser && (
              <select
                value={fundraiser}
                onChange={(e) => setFundraiser(e.target.value)}
                className="filter-select"
              >
                <option value="all">All Donations</option>
                <option value="nonFundraiser">Non-Fundraiser Donations</option>
                {campaigns.map((camp: any) => (
                  <option key={camp.id} value={camp.id}>{camp.name}{camp.archive ? ' (Inactive)' : ''}</option>
                ))}
              </select>
            )}
          </div>
        </div>

        {!hideDownloadReport && (
          <div className="table-actions">
            <button className="action-btn primary" onClick={handleDownloadReport}>
              Download Report
            </button>
          </div>
        )}
      </div>

      <div className="table-wrapper">
        {!isOnboardingComplete ?
          <><div style={{height: '150px', display: 'flex', justifyContent: 'center', alignItems: 'center' }} className='no-data-to-show'>
            Connect Stripe or PayPal account in Connect Payments to start receiving donations.&nbsp;
            <Link to={'/connect-payments'}>Connect Payments</Link>
          </div></> 
        : (!sortedDonations || sortedDonations.length === 0 
          ? <p style={{fontWeight: 700, fontSize: '18px', height: '150px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>You don’t have any donations to show</p>
          :<>
          <table className="donations-table">
            <thead>
              <tr>
                <th onClick={() => handleSort('createdAt')} className="sortable">
                  Date
                  <span className={`sort-indicator${sortField === 'createdAt' ? '' : ' sort-indicator-inactive'}`}>
                    {sortField === 'createdAt' ? (sortDirection === 'asc' ? '↑' : '↓') : '↓'}
                  </span>
                </th>
                <th onClick={() => handleSort('amount')} className="sortable">
                  Amount
                  <span className={`sort-indicator${sortField === 'amount' ? '' : ' sort-indicator-inactive'}`}>
                    {sortField === 'amount' ? (sortDirection === 'asc' ? '↑' : '↓') : '↓'}
                  </span>
                </th>
                <th onClick={() => handleSort('name')} className="sortable">
                  Name
                  <span className={`sort-indicator${sortField === 'name' ? '' : ' sort-indicator-inactive'}`}>
                    {sortField === 'name' ? (sortDirection === 'asc' ? '↑' : '↓') : '↓'}
                  </span>
                </th>
                <th onClick={() => handleSort('lastName')} className="sortable">
                  Last Name
                  <span className={`sort-indicator${sortField === 'lastName' ? '' : ' sort-indicator-inactive'}`}>
                    {sortField === 'lastName' ? (sortDirection === 'asc' ? '↑' : '↓') : '↓'}
                  </span>
                </th>
                <th onClick={() => handleSort('donorEmail')} className="sortable">
                  Email
                  <span className={`sort-indicator${sortField === 'donorEmail' ? '' : ' sort-indicator-inactive'}`}>
                    {sortField === 'donorEmail' ? (sortDirection === 'asc' ? '↑' : '↓') : '↓'}
                  </span>
                </th>
                {!hideZipCode && <th>Zip Code</th>}
                <th>Fundraiser</th>
                <th>Type</th>
                <th>Status</th>
                <th>Thank Supporter</th>
              </tr>
            </thead>
            <tbody>
              {(isDashboardPage ? sortedDonations?.slice(0, 10) : sortedDonations).map((donation, index) => (
                <tr key={(donation as any)._id || (donation as any).id || index}>
                  {/* <td>{formatDate((donation as any).createdAt || (donation as any).date)}</td> */}
                  <td>{formatDisplayDate((donation as any).createdAt || (donation as any).date)}</td>
                  <td className="amount-cell">{formatCurrency((donation as any).amount)}</td>
                  <td>{(donation as any).name || (donation as any).firstName || ''}</td>
                  <td>{(donation as any).lastName || ''}</td>
                  <td className="email-cell">{(donation as any).donorEmail || (donation as any).email || ''}</td>
                  {!hideZipCode && <td>{(donation as any).zipCode || ''}</td>}
                  <td className="fundraiser-cell">
                    {(donation as any).fundraiser && (donation as any).campaignId ? (
                      <>
                        <Link
                          to={
                            (donation as any).isDonorCreated
                              ? (donation as any).campaignSlug
                                ? `/fundraiser/${(donation as any).campaignSlug}`
                                : `/fundraiser/${(donation as any).npId}/${(donation as any).campaignId}`
                              : (donation as any).campaignSlug
                                ? `/donation/${(donation as any).campaignSlug}`
                                : `/donation/${(donation as any).npId}/${(donation as any).campaignId}`
                          }
                        >
                          {(donation as any).fundraiser}
                        </Link>
                        {(donation as any).isDonorCreated && (donation as any).organizerEmail && (
                          <a
                            href={`mailto:${(donation as any).organizerEmail}`}
                            className="donations-table-organizer-badge"
                            onClick={(e) => e.stopPropagation()}
                            title={`Email ${(donation as any).organizerName}`}
                          >
                            By: {(donation as any).organizerName || 'Donor'}
                          </a>
                        )}
                      </>
                    ) : (donation as any).fundraiser ? (
                      (donation as any).fundraiser
                    ) : (
                      'Direct Donation'
                    )}
                  </td>
                  <td>
                    <span
                      style={{ color: getTypeColor((donation as any).type || 'One time') }}
                      className={`badge b-p-m ${(donation as any).type === 'Recurring' ? 'recuring' : 'onetime'}`}>
                      {(donation as any).type || 'One time'}
                    </span>
                  </td>
                  <td>
                    <span className={`badge text-capitalize ${getStatusColor((donation as any).status || 'Completed')}`}>
                      {(donation as any)?.status?.toLowerCase() === 'completed' ? 'Completed' : (donation as any)?.status?.toLowerCase() === 'active' ? 'Completed' : (donation as any)?.status?.toLowerCase() === 'trailing' ? 'Completed' : (donation as any)?.status?.toLowerCase() === 'succeeded' ? 'Completed' : (donation as any)?.status}
                    </span>
                  </td>
                  <td>
                    {((donation as any).donorEmail || (donation as any).email) !== 'Anonymous' && (
                      <img onClick={() => window.location.href = `mailto:${(donation as any).donorEmail || (donation as any).email}`}
                        className="email-btn"
                        title="Send Email"
                        style={{ background: 'none', width: '20px', height: "14px", border: 'none', cursor: 'pointer', fontSize: '16px' }} src="/icons/email.png" alt="" />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>)}
      </div>
      <div className="table-footer">
        <div className="table-summary">
          <span className="summary-text">
            {path === 'dashboard' ?
              <>
                To see all donations,
                <Link to={'/manage-pay'}> go to Manage Donations.</Link>
              </>
              :
              // Independent of the `title` heading prop ("Donations" vs "Donation
              // History") — splicing that in read as "Showing 3 donation history".
              `Showing ${sortedDonations.length} donation${sortedDonations.length === 1 ? '' : 's'}`}
          </span>
        </div>
        <div className="table-total">
          <span className="total-label">Total:</span>
          <span className="total-amount">{formatCurrency(totalAmount)}</span>
        </div>
      </div>
    </div>
  );
};

export default DonationsTable;